import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { getRecommendations } from '../services/ListingService';
import ListingCard from './ListingCard';
import LoadingComponent from "../components/LoadingComponent";

const buildContextQueryForSimilar = () => {
  try {
    const raw = localStorage.getItem('lastSearchParams') || '';
    if (!raw) return '';
    const params = new URLSearchParams(raw);
    // remove pagination/sorting noise for recommendations
    params.delete('page');
    params.delete('sort');
    // if context is empty, don't add anything
    const asString = params.toString();
    return asString;
  } catch (e) {
    return '';
  }
};

function Recommendations({
  limit = 10,
  title = "Rekomendacje dla Ciebie",
  type = 'personalized',
  listingId = null,
}) {
  const [endpoint, setEndpoint] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (type === 'similar' && listingId) {
      const ctx = buildContextQueryForSimilar();
      setEndpoint(`/recommendations/similar/${listingId}?limit=${limit}${ctx ? `&${ctx}` : ''}`);
    } else if (type === 'trending') {
      setEndpoint(`/recommendations/trending?limit=${limit}`);
    } else {
      setEndpoint(`/recommendations?limit=${limit}`);
    }
  }, [type, listingId, limit]);

  useEffect(() => {
    if (!endpoint) return;
    const loadRecommendations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const contextQuery = type === 'similar' ? buildContextQueryForSimilar() : '';
        const { data, meta } = await getRecommendations({ type, listingId, limit, contextQuery });
        if (meta) {
          console.warn('Recommendations warning:', meta);
        }
        setRecommendations(data);
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecommendations();
  }, [endpoint, type, listingId, limit]);

  if (!endpoint) return null;
  if (error) return null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <LoadingComponent />
      </Box>
    );
  }

  if (!recommendations || !Array.isArray(recommendations) || recommendations.length === 0) {
    return null;
  }

  const scrollLeft = () => {
    const el = scrollRef.current;
    if (el) {
      el.scrollBy({ left: -el.clientWidth * 0.8, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    const el = scrollRef.current;
    if (el) {
      el.scrollBy({ left: el.clientWidth * 0.8, behavior: "smooth" });
    }
  };

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
        {title}
      </Typography>

      {/* Kontener slidera + strzałek */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* LEWA strzałka */}
        <IconButton
          onClick={scrollLeft}
          sx={{
            flex: "0 0 auto",
            backgroundColor: "rgba(255,255,255,0.8)",
            boxShadow: 1,
            "&:hover": { backgroundColor: "white" },
            mr: 1,
          }}
        >
          <IconChevronLeft />
        </IconButton>

        {/* Lista ogłoszeń — przewijana poziomo */}
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            placeContent: "center space-evenly",
            gap: 6,
            overflowX: "auto",
            scrollBehavior: "smooth",
            flex: "1 1 auto",
            pb: 1,
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {recommendations.map((recommendation) => {
            const listing = recommendation.listing || recommendation;
            if (!listing || !listing.id) return null;
            return (
             <Box 
                key={listing.id}
                sx={{ 
                  display: 'flex',
                  flex: "0 0 300px",   // STAŁA szerokość
                  width: "300px",
                }}
              >
                <ListingCard listing={listing} />
              </Box>
            );
          })}
        </Box>

        {/* PRAWA strzałka */}
        <IconButton
          onClick={scrollRight}
          sx={{
            flex: "0 0 auto",
            backgroundColor: "rgba(255,255,255,0.8)",
            boxShadow: 1,
            "&:hover": { backgroundColor: "white" },
            ml: 1,
          }}
        >
          <IconChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
}

export default Recommendations;
